import { MessageSquare, NotebookPen } from 'lucide-react'
import { Button, Tooltip } from '@highlight/ui'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'
import {
  useAudioNoteActiveNoteId,
  useAudioNoteAudioSessionId,
  useIsAudioNoteSaving,
  useIsAudioNoteStarting,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useMeetingNotepadClaim } from '../../../views/meeting-view/use-meeting-notepad-claim'
import { useMeetingNotepadToggle } from '../../../views/meeting-view/use-meeting-notepad'
import { useLiveMeetingNotepadKey } from '../../../views/meeting-view/use-live-meeting-notepad-key'
import { SidepanelAudioPill } from './audio-pill'

/**
 * The live meeting's controls on the left of the floating bar: the audio pill
 * and its notepad, wherever the open view hasn't claimed the row itself (Home,
 * History, …) and inside the Action View's own row (Figma 8004:32399).
 *
 * A recording doesn't pause because you went to look at something, so the
 * controls follow you rather than being something to navigate back for — and
 * the pill drops its timer wherever it shares the row with a view's own
 * controls, which is what leaves room for them.
 *
 * Uses the same identity precedence as the Meeting View without mounting that
 * view's query-heavy model for two floating controls.
 */
export function SidepanelMeetingCluster({ showElapsedTime = true }: { showElapsedTime?: boolean }) {
  const notepadKey = useLiveMeetingNotepadKey()
  const audioSessionId = useAudioNoteAudioSessionId()
  const audioNoteId = useAudioNoteActiveNoteId()
  const { isOpen: isNotepadOpen, toggle: toggleNotepad } = useMeetingNotepadToggle(notepadKey, {
    audioSessionId: audioSessionId ?? undefined,
    audioNoteId: audioNoteId ?? undefined,
  })
  useMeetingNotepadClaim(notepadKey)
  // The pill's two bookend states: starting, with no recording to take notes
  // against yet, and saving, with one on its way out. The pill collapses to a
  // spinner for both, and the notepad goes with it.
  const isNotesStarting = useIsAudioNoteStarting()
  const isNotesSaving = useIsAudioNoteSaving()
  const isNotesSettling = isNotesStarting || isNotesSaving

  return (
    <div className="pointer-events-auto flex shrink-0 items-center gap-2">
      <SidepanelAudioPill showElapsedTime={showElapsedTime} />
      {/* One button, two directions — the same swap the Meeting View's own
          notepad button makes: it shows where it takes you, so while the
          notepad holds the footer it offers the chat back. Which is why an open
          notepad keeps its button through the bookends: this toggle is the only
          thing that clears the footer claim, so hiding it mid-save would strand
          the notes with no way back to the chat input. */}
      {(isNotepadOpen || !isNotesSettling) && (
        <Tooltip>
          {/* Matches the Meeting View's notepad button, since this cluster follows the user between views. */}
          <Tooltip.Trigger
            render={
              <Button
                variant="outline"
                shape="square"
                className={FLOATING_FILL_CLASS}
                aria-label={isNotepadOpen ? 'Close notepad' : 'Notepad'}
                aria-pressed={isNotepadOpen}
                onClick={toggleNotepad}
              >
                {isNotepadOpen ? <MessageSquare aria-hidden="true" /> : <NotebookPen aria-hidden="true" />}
              </Button>
            }
          />
          <Tooltip.Content>{isNotepadOpen ? 'Chat' : 'Notepad'}</Tooltip.Content>
        </Tooltip>
      )}
    </div>
  )
}
